import { useState, useCallback, useEffect } from "react";

// Tracks which storage keys have ever been explicitly saved by the user.
// Once a key has been saved, we NEVER re-inject default items into arrays.
const EVER_SAVED_KEY = "ma_ever_saved_keys";

function getEverSavedKeys(): Set<string> {
  try {
    const raw = localStorage.getItem(EVER_SAVED_KEY);
    if (raw) return new Set(JSON.parse(raw));
  } catch { /* ignore */ }
  return new Set();
}

function markKeyAsSaved(key: string) {
  try {
    const keys = getEverSavedKeys();
    keys.add(key);
    localStorage.setItem(EVER_SAVED_KEY, JSON.stringify([...keys]));
  } catch { /* ignore */ }
}

function clearEverSavedKey(key: string) {
  try {
    const keys = getEverSavedKeys();
    keys.delete(key);
    localStorage.setItem(EVER_SAVED_KEY, JSON.stringify([...keys]));
  } catch { /* ignore */ }
}

/**
 * Merges saved data with new defaults so that:
 * - For arrays: ONLY merges new default items if this key has NEVER been saved by the user.
 *   Once the user has saved (committed), the saved array is used as-is — deletions are respected.
 * - For objects: any keys in default missing from saved are added
 * - Primitive values: saved value wins
 */
function mergeWithDefaults<T>(saved: T, defaultValue: T, key: string): T {
  if (saved === null || saved === undefined) return defaultValue;

  if (
    Array.isArray(defaultValue) &&
    Array.isArray(saved) &&
    defaultValue.length > 0 &&
    typeof (defaultValue as unknown[])[0] === "object" &&
    (defaultValue as Record<string, unknown>[])[0] !== null &&
    "id" in (defaultValue as Record<string, unknown>[])[0]
  ) {
    // If the user has ever explicitly saved this key, trust the saved array completely.
    // This means deletions are permanent.
    const everSaved = getEverSavedKeys();
    if (everSaved.has(key)) {
      return saved;
    }
    // First load ever — merge in any new default items not yet in saved
    const savedArr = saved as Record<string, unknown>[];
    const defaultArr = defaultValue as Record<string, unknown>[];
    const savedIds = new Set(savedArr.map((item) => item.id));
    const newItems = defaultArr.filter((item) => !savedIds.has(item.id));
    return [...savedArr, ...newItems] as unknown as T;
  }

  if (
    typeof defaultValue === "object" &&
    !Array.isArray(defaultValue) &&
    defaultValue !== null &&
    typeof saved === "object" &&
    !Array.isArray(saved) &&
    saved !== null
  ) {
    const result = { ...(saved as Record<string, unknown>) };
    const defaultObj = defaultValue as Record<string, unknown>;
    for (const key of Object.keys(defaultObj)) {
      if (!(key in result)) {
        result[key] = defaultObj[key];
      }
    }
    return result as T;
  }

  return saved;
}

// Global staging store: key -> staged (unsaved) value
const stagingStore: Map<string, unknown> = new Map();
// Listeners to notify when staging store changes
const stagingListeners: Map<string, Set<() => void>> = new Map();

function notifyListeners(key: string) {
  stagingListeners.get(key)?.forEach((fn) => fn());
}

/** Directly set a value in the staging store (for use outside of useAdminStorage hook) */
export function stagingStoreSet(key: string, value: unknown): void {
  stagingStore.set(key, value);
}

/** Get a value from the staging store if it exists */
export function stagingStoreGet(key: string): unknown | undefined {
  return stagingStore.get(key);
}

/** Notify listeners for a key (for use outside of useAdminStorage hook) */
export function stagingStoreNotify(key: string): void {
  notifyListeners(key);
}

/** Notify ALL staging listeners to re-read from localStorage.
 *  Call this after an external sync (e.g. Supabase fetch) writes to localStorage. */
export function broadcastStorageSync(): void {
  stagingListeners.forEach((listeners) => {
    listeners.forEach((fn) => fn());
  });
}

/** Get a snapshot of all currently staged (unsaved) changes as a key->value map */
export function getStagedSnapshot(): Record<string, unknown> {
  const snapshot: Record<string, unknown> = {};
  stagingStore.forEach((value, key) => {
    snapshot[key] = value;
  });
  return snapshot;
}

/** Get ALL current content (localStorage + staged) as a key->value map for Supabase sync */
export function getAllCurrentContent(keys: string[]): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  keys.forEach((key) => {
    if (stagingStore.has(key)) {
      result[key] = stagingStore.get(key);
    } else {
      try {
        const stored = localStorage.getItem(key);
        if (stored) {
          try {
            result[key] = JSON.parse(stored);
          } catch {
            // Handle raw string values (e.g. ma_logo_font stored as plain string)
            result[key] = stored;
          }
        }
      } catch { /* ignore */ }
    }
  });
  return result;
}

/** Commit all staged changes to localStorage */
export function commitAllStagedChanges(): string[] {
  const keys: string[] = [];
  stagingStore.forEach((value, key) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      markKeyAsSaved(key);
      keys.push(key);
    } catch {
      // ignore
    }
  });
  stagingStore.clear();
  return keys;
}

/** Discard all staged changes (does NOT touch localStorage) */
export function discardAllStagedChanges() {
  stagingStore.clear();
  // Notify all listeners so components re-read from localStorage
  stagingListeners.forEach((listeners) => listeners.forEach((fn) => fn()));
}

/** Reset all localStorage keys and staged changes to defaults */
export function resetAllToDefaults(keys: string[]) {
  keys.forEach((key) => {
    try { localStorage.removeItem(key); } catch { /* ignore */ }
    clearEverSavedKey(key);
  });
  stagingStore.clear();
  stagingListeners.forEach((listeners) => listeners.forEach((fn) => fn()));
}

export function useAdminStorage<T>(
  key: string,
  defaultValue: T
): [T, (val: T) => void, () => void] {
  const getPersistedValue = useCallback((): T => {
    try {
      const stored = localStorage.getItem(key);
      if (stored) {
        const parsed = JSON.parse(stored) as T;
        return mergeWithDefaults(parsed, defaultValue, key);
      }
    } catch {
      // ignore
    }
    return defaultValue;
  }, [key, defaultValue]);

  const getCurrentValue = useCallback((): T => {
    if (stagingStore.has(key)) {
      return stagingStore.get(key) as T;
    }
    return getPersistedValue();
  }, [key, getPersistedValue]);

  const [value, setValue] = useState<T>(getCurrentValue);

  // Subscribe to staging store changes
  useEffect(() => {
    if (!stagingListeners.has(key)) {
      stagingListeners.set(key, new Set());
    }
    const listener = () => {
      setValue(getCurrentValue());
    };
    stagingListeners.get(key)!.add(listener);
    return () => {
      stagingListeners.get(key)?.delete(listener);
    };
  }, [key, getCurrentValue]);

  // Stage the change in memory only (does NOT write to localStorage)
  const stage = useCallback(
    (val: T) => {
      stagingStore.set(key, val);
      setValue(val);
      notifyListeners(key);
    },
    [key]
  );

  const reset = useCallback(() => {
    stagingStore.delete(key);
    try { localStorage.removeItem(key); } catch { /* ignore */ }
    clearEverSavedKey(key);
    const fresh = defaultValue;
    setValue(fresh);
    notifyListeners(key);
  }, [key, defaultValue]);

  return [value, stage, reset];
}

// ─── Export to Source Code ──────────────────────────────────────────────────

/** All known content keys that should be exportable and syncable to Supabase */
export const EXPORTABLE_CONTENT_KEYS = [
  "ma_hero",
  "ma_whats_included",
  "ma_spaces_v2",
  "ma_application_v2",
  "ma_rules",
  "ma_rules_cta",
  "ma_mission",
  "ma_meet_makers_header",
  "ma_artists_shared",
  "ma_faq_v2",
  "ma_contact_v3",
  "ma_footer_v3",
  "ma_nav_links",
  "ma_notify_banner",
  "ma_section_visibility",
  "ma_section_order",
  "ma_section_data_map",
  "ma_section_labels",
  "ma_logo_font",
  "ma_logo_config",
  "ma_gallery_deleted_builtins",
  "ma_site_notifications",
  "ma_custom_pages_v1",
  "ma_testimonials_v2",
  "ma_leave_review_v3",
  "ma_artists_meta",
  "ma_careers_v1",
  "ma_submitted_reviews",
  "ma_site_meta",
  "ma_admin_password",
  "ma_instagram_posts",
  "ma_gallery_hero_ref",
  "ma_why_we_exist",
];

/** Read the current value for a key (localStorage + staging) */
export function getCurrentValueForKey<T>(key: string, defaultValue: T): T {
  if (stagingStore.has(key)) {
    return stagingStore.get(key) as T;
  }
  try {
    const stored = localStorage.getItem(key);
    if (stored) {
      const parsed = JSON.parse(stored) as T;
      return mergeWithDefaults(parsed, defaultValue, key);
    }
  } catch { /* ignore */ }
  return defaultValue;
}

/** Generate a TypeScript source file string from all localStorage edits */
export function generateSourceCodeExport(defaultsMap: Record<string, unknown>): string {
  const exports: Record<string, unknown> = {};

  EXPORTABLE_CONTENT_KEYS.forEach((key) => {
    const defaultVal = defaultsMap[key];
    if (defaultVal === undefined) return;
    const current = getCurrentValueForKey(key, defaultVal);
    // Only include if it differs from default
    if (JSON.stringify(current) !== JSON.stringify(defaultVal)) {
      exports[key] = current;
    }
  });

  if (Object.keys(exports).length === 0) {
    return "// No edits have been made yet. Make some changes in admin mode and save them first.";
  }

  const lines: string[] = [
    "// Copy this entire block into src/data/siteContent.ts",
    "// Replace the corresponding default exports with these updated values.",
    "// Then rebuild and redeploy your site.",
    "",
  ];

  Object.entries(exports).forEach(([key, value]) => {
    const varName = keyToVarName(key);
    lines.push(`export const ${varName} = ${serializeValue(value)};`);
    lines.push("");
  });

  return lines.join("\n");
}

function keyToVarName(key: string): string {
  const map: Record<string, string> = {
    "ma_hero": "defaultHero",
    "ma_whats_included": "defaultWhatsIncluded",
    "ma_spaces_v2": "defaultSpacesSection",
    "ma_application_v2": "defaultApplication",
    "ma_rules": "defaultRules",
    "ma_rules_cta": "defaultRulesCTA",
    "ma_mission": "defaultMission",
    "ma_meet_makers_header": "defaultMeetMakers",
    "ma_artists_shared": "defaultArtists",
    "ma_faq_v2": "defaultFAQ",
    "ma_contact_v3": "defaultContact",
    "ma_footer_v3": "defaultFooter",
    "ma_nav_links": "defaultNavLinks",
    "ma_notify_banner": "defaultNotifyBanner",
    "ma_section_visibility": "defaultSectionVisibility",
    "ma_section_order": "defaultSectionOrder",
    "ma_section_data_map": "defaultSectionDataMap",
    "ma_section_labels": "defaultSectionLabels",
    "ma_logo_font": "defaultLogoFont",
    "ma_logo_config": "defaultLogoConfig",
    "ma_gallery_deleted_builtins": "defaultDeletedBuiltins",
    "ma_site_notifications": "defaultNotifications",
    "ma_custom_pages_v1": "defaultCustomPages",
    "ma_testimonials_v2": "defaultTestimonialsPage",
    "ma_leave_review_v3": "defaultLeaveReviewPage",
    "ma_artists_meta": "defaultArtistsMeta",
    "ma_submitted_reviews": "defaultSubmittedReviews",
    "ma_site_meta": "defaultSiteMeta",
    "ma_admin_password": "defaultAdminPassword",
    "ma_instagram_posts": "defaultInstagramPosts",
    "ma_why_we_exist": "defaultWhyWeExist",
  };
  return map[key] || key;
}

function serializeValue(value: unknown): string {
  return JSON.stringify(value, null, 2)
    .replace(/"([^"]+)":/g, "$1:")
    .replace(/"/g, "'");
}